import { useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faChevronDown, faArrowsRotate } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import usePrefStore from '../stores/prefs'
import { AppWriteContext } from '../Contexts/AppWriteContext'
import useTranslation from '../Hooks/useTranslation'
import Button from './Button'
import Confirm from './Confirm'
import ProgressPopup from './ProgressPopup'
import styles from './ImpersonationBanner.module.scss'

// Bloc repliable en pied du menu latéral affiché pendant une impersonation active (messika-pwa#10, #15).
function ImpersonationBanner() {
   const { trans } = useTranslation()
   const { doLogout, initialFetch } = { ...AppWriteContext() } as any
   const target = usePrefStore((s) => s.impersonateTarget)
   const operator = usePrefStore((s) => s.impersonateOperator)
   const isLogged = usePrefStore((s) => s.isLogged)
   const [confirm, setConfirm] = useState(false)
   const [open, setOpen] = useState(false)
   const [refreshing, setRefreshing] = useState(false)
   const [total, setTotal] = useState(0)
   const [done, setDone] = useState(0)

   // Synchro incrémentale à la demande (même appel que le login, sans navigation ni push).
   const refresh = () => {
      if (refreshing) return
      setTotal(0)
      setDone(0)
      setRefreshing(true)
      initialFetch(
         false,
         () => {
            setRefreshing(false)
            toast.success(trans('?impersonate_refresh_done'), { position: 'top-right', autoClose: 2500, theme: 'colored' })
         },
         (err: any) => {
            setRefreshing(false)
            toast.error(typeof err === 'string' ? err : (err?.message ?? trans('?Erreur inconnue')), { position: 'top-right', autoClose: 6000, theme: 'colored' })
         },
         setTotal,
         () => setDone((d: number) => d + 1),
         () => { }
      )
   }

   if (!target || !operator || !isLogged) return null

   return (
      <>
         <div className={styles.banner}>
            <div className={styles.head_row}>
               <button type="button" className={styles.refresh} onClick={refresh} disabled={refreshing}
                  title={trans('?impersonate_refresh')} aria-label={trans('?impersonate_refresh')}>
                  <FontAwesomeIcon icon={faArrowsRotate} spin={refreshing} />
               </button>
               <button type="button" className={styles.header} onClick={() => setOpen((o) => !o)}
                  title={trans('?impersonate_toggle')} aria-label={trans('?impersonate_toggle')} aria-expanded={open}>
                  <strong className={styles.title}>{trans('?impersonate_title')}</strong>
                  <FontAwesomeIcon icon={open ? faChevronDown : faChevronUp} />
               </button>
            </div>
            {open && (
               <>
                  <div className={styles.line}>
                     <span>{trans('?impersonate_as_label')}</span>
                     <span className={styles.email}>{target}</span>
                  </div>
                  <div className={styles.line}>
                     <span>{trans('?impersonate_operator')}</span>
                     <span className={styles.email}>{operator}</span>
                  </div>
                  <Button small className={styles.back} onClick={() => setConfirm(true)}>{trans('?impersonate_back')}</Button>
               </>
            )}
         </div>
         {refreshing && createPortal(
            <ProgressPopup title={trans('?impersonate_refresh_progress')} done={done} total={total} />,
            document.body
         )}
         {confirm && createPortal(
            <Confirm
               title={trans('?impersonate_back_title')}
               destructive
               okLabel={trans('?Continuer')}
               onCancel={() => setConfirm(false)}
               onOK={() => { setConfirm(false); doLogout('exit') }}
            >
               <p>{trans('?impersonate_back_warn')}<br />{trans('?impersonate_back_warn_2')}</p>
            </Confirm>,
            document.body
         )}
      </>
   )
}

export default ImpersonationBanner
